import {type ReactNode, useState} from 'react';

import Tooltip from '@mui/material/Tooltip';

import {OptixTableActionsButton} from './OptixTableActionsButton';
import {OptixButton, OptixIconButton} from '../../../../button';
import {OptixDialog} from '../../../../dialog/OptixDialog';
import {type RowAction} from '../../../OptixTable.types';
import {useOptixTableContext} from '../../../OptixTableContext';

function resolveActionIcon<TData>(
  icon: RowAction<TData>['icon'],
  row: TData,
): ReactNode {
  return typeof icon === 'function' ? icon(row) : icon;
}

interface OptixTableEditActionProps<TData extends object> {
  row: TData;
}

export function OptixTableRowAction<TData extends object>({
  row,
}: OptixTableEditActionProps<TData>): JSX.Element | undefined {
  const {enableRowActions, rowActions} = useOptixTableContext();
  if (enableRowActions) {
    return <OptixTableRowActionsAction row={row} rowActions={rowActions} />;
  }
}

function OptixTableRowActionsAction<TData extends object>({
  row,
  rowActions,
}: OptixTableEditActionProps<TData> & {
  rowActions: Array<RowAction<unknown>> | undefined;
}): JSX.Element | undefined {
  const {loading: loadingTable} = useOptixTableContext();
  if (!rowActions || rowActions.length === 0) return;

  return (
    <>
      {rowActions
        .filter((action) => {
          if (typeof action.hidden === 'function') return !action.hidden(row);
          return !action.hidden;
        })
        .map((action) =>
          action.confirmationMessage != null ? (
            <ConfirmRowAction
              key={action.label}
              action={action}
              row={row}
              disabled={loadingTable}
            />
          ) : (
            <OptixTableActionsButton
              key={action.label}
              text={action.label}
              icon={resolveActionIcon(action.icon, row)}
              iconOnly={action.iconOnly}
              onClick={() => {
                void action.onClick?.(row);
              }}
            />
          ),
        )}
    </>
  );
}

function ConfirmRowAction<TData extends object>({
  action,
  row,
  disabled,
}: {
  action: RowAction<unknown>;
  row: TData;
  disabled?: boolean;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const icon = resolveActionIcon(action.icon, row);

  const handleAccept = async (): Promise<void> => {
    setAccepting(true);
    try {
      const result = action.onClick?.(row as unknown);
      if (result instanceof Promise) {
        await result;
      }
    } finally {
      setAccepting(false);
      setOpen(false);
    }
  };

  const triggerButton =
    action.iconOnly === true && icon != null ? (
      <Tooltip title={action.label}>
        <span>
          <OptixIconButton
            size="small"
            color="inherit"
            disabled={disabled}
            onClick={() => {
              setOpen(true);
            }}
          >
            {icon}
          </OptixIconButton>
        </span>
      </Tooltip>
    ) : (
      <OptixButton
        text={action.label}
        startIcon={icon}
        onClick={() => {
          setOpen(true);
        }}
        disabled={disabled}
        color="inherit"
        variant="text"
        size="small"
      />
    );

  return (
    <>
      {triggerButton}
      <OptixDialog
        open={open}
        onClose={() => {
          if (!accepting) {
            setOpen(false);
          }
        }}
        title="Confirm"
        message={action.confirmationMessage}
        enableAccept
        enableCloseOnAccept={false}
        acceptText="Confirm"
        enableDeny
        enableCloseOnDeny
        denyText="Cancel"
        acceptProps={{loading: accepting, disabled: accepting, color: 'error'}}
        onAccept={() => {
          void handleAccept();
        }}
      />
    </>
  );
}
